/**
 * royalty-api.ts
 *
 * API client for royalty statement endpoints
 */

import { apiRequest } from './api';

/**
 * Thrown when the backend blocks a send as a probable duplicate (HTTP 409 duplicate_send).
 * The caller can prompt the user and retry the same send with { force: true }.
 */
export class DuplicateSendError extends Error {
  duplicatePeriods: string[];
  constructor(message: string, duplicatePeriods: string[]) {
    super(message);
    this.name = 'DuplicateSendError';
    this.duplicatePeriods = duplicatePeriods;
  }
}

/** Parse a send response, converting a 409 duplicate_send into a DuplicateSendError. */
async function parseSendResponse(response: Response, fallbackMsg: string): Promise<any> {
  if (response.ok) return response.json();
  const error = await response.json().catch(() => ({}));
  if (response.status === 409 && error?.error === 'duplicate_send') {
    throw new DuplicateSendError(error.message || 'This statement was already sent recently.', error.duplicatePeriods || []);
  }
  throw new Error(error.message || fallbackMsg);
}

// Types
export interface RoyaltyPeriod {
  settlementPeriod: string;
  authorCount: number;
  lineItemCount: number;
  totalRoyalties: number;
}

export interface RoyaltyStatementSummary {
  settlementPeriod: string;
  creditorId: number;
  authorName: string;
  authorFirstName: string | null;
  authorLastName: string | null;
  accountNumber: number;
  currency: string;
  lineItemCount: number;
  totalCopies: number;
  totalReturns: number;
  totalRoyalties: number;
  titleCount: number;
}

export interface RoyaltyStatementLine {
  settlementPeriod: string;
  accountId: number;
  creditorId: number;
  accountNumber: number;
  authorName: string;
  authorFirstName: string | null;
  authorLastName: string | null;
  currency: string;
  isbn: string;
  copies: number;
  returns: number;
  slidingRate: number | null;
  royalty: number;
  revenue: number | null;
  salesType: string | null;
  territory: string | null;
  distributionChannel: string | null;
  // Enhanced fields for Power BI parity
  ungross: number | null;
  rateOrigin: number | null;
  royaltyBase: number | null;
  slidingBaseAmount: number | null;
  amountReceived: number | null;
  totalRevenue: number | null;
  payeeShare: number | null;
  netUnits: number | null;
  titleDetails: string | null;
  publicationDate: string | null;
  isPreSapMigration: boolean;
}

/**
 * Settlement header with payment summary (matches Power BI pl_royalty_settlement)
 */
export interface RoyaltySettlementHeader {
  accountNumber: number;
  creditorId: number;
  settlementPeriod: string;
  authorName: string | null;
  ownershipRoyalty: string | null;
  address: string | null;
  owner: string | null;
  lastAccountBalance: number;
  grossAmountPaid: number;
  vatAmount: number;
  subTotal: number;
  netPayment: number;
}

export interface RoyaltyStatementData {
  summary: RoyaltyStatementSummary;
  header: RoyaltySettlementHeader | null;  // Payment summary (when SAP data available)
  lines: RoyaltyStatementLine[];
  authorEmail: string | null;
  contactId: string | null;
  periodLabel?: string;    // "Q1 2024" — overrides auto-formatted period
  filenameTag?: string;    // "2024-Q1" — overrides auto-formatted filename
}

export interface AuthorsResponse {
  authors: RoyaltyStatementSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface EmailResult {
  success: boolean;
  authorEmail: string;
  pdfSizeBytes?: number;
  errorMessage?: string;
}

// Types for person-first search
export interface AuthorSearchResult {
  creditorId: number;
  authorName: string;
  authorFirstName: string | null;
  authorLastName: string | null;
  accountNumber: number;
  email: string | null;
  periodCount: number;
  latestPeriod: string;
  totalRoyaltiesAllTime: number;
}

export interface AuthorSearchResponse {
  authors: AuthorSearchResult[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuthorPeriodSummary {
  settlementPeriod: string;
  currency: string;
  totalRoyalties: number;
  lineItemCount: number;
  titleCount: number;
}

// Account summary combining Klopotek royalties and SAP financials
export interface AccountSummary {
  creditorId: number;
  accountNumber: number;
  authorName: string;
  authorFirstName: string | null;
  authorLastName: string | null;
  currency: string;            // PAYMENT currency
  statementCurrency: string;   // STATEMENT currency — the ccy of the share totals below
  // Klopotek royalty totals (author's share, in STATEMENT currency)
  totalRoyalties: number;
  sapComparableRoyaltyTotal: number;
  totalCopies: number;
  totalReturns: number;
  totalTitles: number;
  totalLineItems: number;
  firstPeriod: string | null;
  lastPeriod: string | null;
  // SAP financial summary
  sapBalance: number;
  outstandingBalance: number;
  totalPaymentDocuments: number;
  totalRoyaltyDocuments: number;
  transactionCount: number;
  paymentCount: number;
  firstTransactionDate: string | null;
  lastTransactionDate: string | null;
}

// Quarterly statement summary
export interface QuarterlyStatement {
  creditorId: number;
  accountNumber: number;
  authorName: string;
  currency: string;            // PAYMENT currency
  statementCurrency: string;   // STATEMENT currency — the ccy of the amounts below
  statementYear: number;
  quarterNum: number;
  quarterLabel: string;
  quarterEndDate: string;
  totalCopies: number;
  totalReturns: number;
  totalRoyalty: number;             // gross (statement ccy)
  totalPayeeShare: number;          // author's share (statement ccy) — headline (Q1)
  totalPayeeSharePayCcy: number;    // author's share in payment ccy (payable); 0 when same ccy
  lineItemCount: number;
}

// Overdue royalty row (derived from vw_RoyaltyAccountSummary, sorted by AmountOwed in SQL)
export interface OverdueRoyaltyRow {
  creditorId: number;
  accountNumber: number;
  authorName: string;
  authorFirstName: string | null;
  authorLastName: string | null;
  currency: string;
  totalEarned: number;
  sapBalance: number;
  sapBalanceLocal: number;
  amountOwed: number;
  totalTitles: number;
  firstPeriod: string | null;
  lastPeriod: string | null;
  sapTransactionCount: number;
  sapLastTransaction: string | null;
  // R7 — most-recent email send across all settlement periods for this creditor.
  // Excludes internal sends (authorEmail @peterlang.com) per Tamara's spec.
  lastSentAt: string | null;
  lastSentBy: string | null;
  sendCount: number;
}

export interface OverdueRoyaltiesResponse {
  rows: OverdueRoyaltyRow[];
  total: number;
  limit: number;
  offset: number;
}

// Quarterly statement line item
export interface QuarterlyStatementLine {
  settlementPeriod: string;
  isbn: string;
  copies: number;
  returns: number;
  slidingRate: number | null;
  royalty: number;                  // gross (statement ccy)
  payeeSharePct: number | null;     // co-author split %
  payeeShare: number;               // author's share for this line (statement ccy)
  statementCurrency: string;
  revenue: number | null;
  salesType: string | null;
  territory: string | null;
  distributionChannel: string | null;
}

/**
 * Get all available settlement periods
 */
export async function getSettlementPeriods(): Promise<RoyaltyPeriod[]> {
  const response = await apiRequest('GET', '/api/royalty-statements/periods');

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch settlement periods');
  }

  const data = await response.json();
  return data.periods;
}

/**
 * Get authors for a settlement period with optional search
 */
export async function getAuthorsForPeriod(
  period: string,
  search?: string,
  limit: number = 100,
  offset: number = 0
): Promise<AuthorsResponse> {
  const params = new URLSearchParams({
    period,
    limit: limit.toString(),
    offset: offset.toString()
  });

  if (search) {
    params.set('search', search);
  }

  const response = await apiRequest('GET', `/api/royalty-statements/authors?${params.toString()}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch authors');
  }

  return response.json();
}

/**
 * Get statement data for a specific author and period
 */
export async function getStatementData(
  creditorId: number,
  period: string
): Promise<RoyaltyStatementData> {
  const response = await apiRequest('GET', `/api/royalty-statements/${creditorId}/${period}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch statement data');
  }

  return response.json();
}

/**
 * Get HTML preview of statement
 */
export async function getStatementPreview(
  creditorId: number,
  period: string
): Promise<string> {
  const response = await apiRequest('GET', `/api/royalty-statements/${creditorId}/${period}/preview`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch statement preview');
  }

  return response.text();
}

/**
 * Generate PDF statement and trigger download.
 * @param format - 'klopotek' (default, landscape Klopotek-style) or 'legacy' (portrait)
 */
export async function downloadStatementPdf(
  creditorId: number,
  period: string,
  accountNumber: number,
  format: 'klopotek' | 'legacy' = 'klopotek'
): Promise<void> {
  const response = await apiRequest('POST', `/api/royalty-statements/${creditorId}/${period}/pdf?format=${format}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to generate PDF');
  }

  // Get the PDF blob and trigger download
  const blob = await response.blob();
  const periodDate = new Date(period);
  const filename = format === 'klopotek'
    ? `${accountNumber}-${periodDate.getFullYear()}-${String(periodDate.getMonth() + 1).padStart(2, '0')}.pdf`
    : `royalty-statement-${accountNumber}-${periodDate.getFullYear()}-${String(periodDate.getMonth() + 1).padStart(2, '0')}.pdf`;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * Send statement email to author
 */
export async function sendStatementEmail(
  creditorId: number,
  period: string,
  email?: string,
  sentBy?: string,
  customSubject?: string,
  force?: boolean
): Promise<EmailResult> {
  const response = await apiRequest(
    'POST',
    `/api/royalty-statements/${creditorId}/${period}/email`,
    { email, sentBy, customSubject, force }
  );

  return parseSendResponse(response, 'Failed to send email');
}

// ============================================================================
// Person-First Search API Functions
// ============================================================================

/**
 * Search authors by name, account number, or email
 */
export async function searchAuthors(
  query: string,
  limit: number = 50,
  offset: number = 0
): Promise<AuthorSearchResponse> {
  const params = new URLSearchParams({
    q: query,
    limit: limit.toString(),
    offset: offset.toString()
  });

  const response = await apiRequest('GET', `/api/royalty-statements/search?${params.toString()}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to search authors');
  }

  return response.json();
}

/**
 * Get all settlement periods for a specific author
 */
export async function getAuthorPeriods(creditorId: number): Promise<AuthorPeriodSummary[]> {
  const response = await apiRequest('GET', `/api/royalty-statements/author/${creditorId}/periods`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch author periods');
  }

  const data = await response.json();
  return data.periods;
}

// ============================================================================
// Account Summary and Quarterly Statements API Functions
// ============================================================================

/**
 * Get account summary with lifetime totals (royalties and payments)
 */
export async function getAccountSummary(creditorId: number): Promise<AccountSummary> {
  const response = await apiRequest('GET', `/api/royalty-statements/author/${creditorId}/summary`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch account summary');
  }

  return response.json();
}

/**
 * Get quarterly statement summaries for an author
 */
export async function getQuarterlyStatements(creditorId: number): Promise<QuarterlyStatement[]> {
  const response = await apiRequest('GET', `/api/royalty-statements/author/${creditorId}/quarterly`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch quarterly statements');
  }

  const data = await response.json();
  return data.quarters;
}

/**
 * Get line items for a specific quarter
 */
export async function getQuarterlyLines(
  creditorId: number,
  year: number,
  quarter: number
): Promise<QuarterlyStatementLine[]> {
  const response = await apiRequest('GET', `/api/royalty-statements/author/${creditorId}/quarterly/${year}/${quarter}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch quarterly lines');
  }

  const data = await response.json();
  return data.lines;
}

/**
 * Get quarterly statement data in RoyaltyStatementData shape (for preview dialog)
 */
export async function getQuarterlyStatementData(
  creditorId: number,
  year: number,
  quarter: number
): Promise<RoyaltyStatementData> {
  const response = await apiRequest('GET', `/api/royalty-statements/author/${creditorId}/quarterly/${year}/${quarter}/data`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch quarterly statement data');
  }

  return response.json();
}

/**
 * Generate quarterly PDF statement and trigger download.
 * @param format - 'klopotek' (default, landscape Klopotek-style) or 'legacy' (portrait)
 */
export async function downloadQuarterlyPdf(
  creditorId: number,
  year: number,
  quarter: number,
  accountNumber: number,
  format: 'klopotek' | 'legacy' = 'klopotek'
): Promise<void> {
  const response = await apiRequest('POST', `/api/royalty-statements/author/${creditorId}/quarterly/${year}/${quarter}/pdf?format=${format}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to generate quarterly PDF');
  }

  const blob = await response.blob();
  const filename = format === 'klopotek'
    ? `${accountNumber}-${year}-Q${quarter}.pdf`
    : `royalty-statement-${accountNumber}-${year}-Q${quarter}.pdf`;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * Get overdue royalties (pre-sorted by largest amount in SQL)
 */
export async function getOverdueRoyalties(
  limit: number = 500,
  offset: number = 0
): Promise<OverdueRoyaltiesResponse> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString()
  });

  const response = await apiRequest('GET', `/api/royalty-statements/overdue?${params.toString()}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch overdue royalties');
  }

  return response.json();
}

// ============================================================================
// Author Email Lookup
// ============================================================================

export interface AuthorEmailEntry {
  email: string;
  source: string;
  isMain: boolean;
}

export interface AuthorEmailLookup {
  creditorId: number;
  authorName: string;
  email: string | null;
  contactId: string | null;
  emails?: AuthorEmailEntry[];
  bankAccountLast4?: string | null;
}

// R9 — SB-type SAP payment history
export interface PaymentHistoryRow {
  creditorId: number;
  accountNumber: number;
  authorName: string;
  documentNumber: string;
  documentDate: string;
  netDueDate: string | null;
  documentType: string;
  description: string | null;
  reference: string | null;
  assignment: string | null;
  amountInDocCurr: number;
  documentCurrency: string;
  amountInLocalCurr: number;
  localCurrency: string;
  clearingDocument: number | null;
  paymentStatus: string | null;
}

export interface PaymentHistorySummary {
  creditorId: number;
  entryCount: number;          // every SAP ledger row (all doc types)
  paymentCount: number;        // subset that are payment-type docs
  firstPaymentDate: string | null;
  lastPaymentDate: string | null;
  totalsByCurrency: Record<string, number>;       // balance per document currency
  localTotalsByCurrency: Record<string, number>;  // balance per local (company, e.g. CHF) currency
}

export interface PaymentHistoryResponse {
  rows: PaymentHistoryRow[];
  summary: PaymentHistorySummary;
}

/**
 * Get SB-type SAP payment history for an author (R9 — ALL rows across history per Tamara default)
 */
export async function getPaymentHistory(creditorId: number): Promise<PaymentHistoryResponse> {
  const response = await apiRequest('GET', `/api/royalty-statements/author/${creditorId}/payment-history`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch payment history');
  }

  return response.json();
}

export interface SentStatusEntry {
  settlementPeriod: string;
  lastSentAt: string;
  lastSentBy: string;
  lastEmail: string;
  /** Latest delivery state: 'accepted' (queued/unconfirmed), 'delivered', 'bounced', or legacy 'sent'. */
  lastStatus: string;
  sendCount: number;
}

/**
 * Get sent status for an author's statements (which periods have been emailed)
 */
export async function getSentStatus(creditorId: number): Promise<SentStatusEntry[]> {
  const response = await apiRequest('GET', `/api/royalty-statements/author/${creditorId}/sent-status`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch sent status');
  }

  const data = await response.json();
  return data.statuses;
}

/**
 * Look up the resolved email address for an author from Dataverse
 */
export async function getAuthorEmail(creditorId: number): Promise<AuthorEmailLookup> {
  const response = await apiRequest('GET', `/api/royalty-statements/author/${creditorId}/email`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to lookup author email');
  }

  return response.json();
}

// ============================================================================
// Bulk Operations
// ============================================================================

export interface BulkEmailResult {
  success: boolean;
  authorEmail: string;
  attachmentCount: number;
  periods: string[];
}

/**
 * Download multiple quarterly statements as a zip file.
 * @param format - 'klopotek' (default) or 'legacy'
 */
export async function downloadBulkQuarterlyPdf(
  creditorId: number,
  quarters: { year: number; quarter: number }[],
  accountNumber: number,
  format: 'klopotek' | 'legacy' = 'klopotek'
): Promise<void> {
  const response = await apiRequest('POST', `/api/royalty-statements/author/${creditorId}/quarterly/bulk-pdf?format=${format}`, { quarters });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to generate bulk PDFs');
  }

  const blob = await response.blob();
  const filename = `royalty-statements-${accountNumber}.zip`;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * Send multiple quarterly statements as attachments on a single email
 */
export async function sendBulkQuarterlyEmail(
  creditorId: number,
  quarters: { year: number; quarter: number }[],
  email: string,
  sentBy?: string,
  customMessage?: string,
  customSubject?: string,
  cc?: string[],
  force?: boolean
): Promise<BulkEmailResult> {
  const response = await apiRequest('POST', `/api/royalty-statements/author/${creditorId}/quarterly/bulk-email`, {
    quarters,
    email,
    sentBy,
    customMessage,
    customSubject,
    cc,
    force
  });

  return parseSendResponse(response, 'Failed to send bulk email');
}

// ============================================================================
// R5 — Bulk email queue
// ============================================================================

export interface BulkQueueItem {
  creditorId: number;
  period: string;            // ISO settlement period date
  email?: string;
  sentBy?: string;
  customMessage?: string;
  customSubject?: string;
}

export interface BulkQueueResult {
  enqueueId: string;
  queued: number;
  failed: number;
  results: Array<{ creditorId: number; period: string; messageId?: string; error?: string }>;
}

/**
 * Enqueue many royalty statement emails for async send via the queue consumer.
 * Each item processes independently. Failures are retried (maxDequeueCount=3)
 * and surfaced in the daily failed-summary email next morning.
 */
export async function queueBulkRoyaltyEmails(
  items: BulkQueueItem[],
  sentBy?: string
): Promise<BulkQueueResult> {
  const response = await apiRequest('POST', '/api/royalty-statements/bulk-queue', { items, sentBy });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to enqueue bulk emails');
  }
  return response.json();
}

/**
 * Send quarterly statement email to author
 */
export async function sendQuarterlyEmail(
  creditorId: number,
  year: number,
  quarter: number,
  email?: string,
  sentBy?: string,
  customMessage?: string,
  customSubject?: string,
  cc?: string[],
  force?: boolean
): Promise<EmailResult> {
  const response = await apiRequest(
    'POST',
    `/api/royalty-statements/author/${creditorId}/quarterly/${year}/${quarter}/email`,
    { email, sentBy, customMessage, customSubject, cc, force }
  );

  return parseSendResponse(response, 'Failed to send quarterly email');
}

// ============================================================================
// CC Recipients (A5 / F7) — admin-managed stakeholder CC list
// ============================================================================

export interface CcRecipient {
  id: number;
  emailAddress: string;
  displayName: string | null;
  isActive: boolean;
  createdAt: string;
  createdBy: string | null;
}

export async function getCcRecipients(activeOnly = false): Promise<CcRecipient[]> {
  const response = await apiRequest('GET', `/api/royalty-cc-recipients${activeOnly ? '?activeOnly=true' : ''}`);
  if (!response.ok) throw new Error('Failed to load CC recipients');
  const data = await response.json();
  return data.recipients;
}

export async function addCcRecipient(emailAddress: string, displayName?: string): Promise<CcRecipient> {
  const response = await apiRequest('POST', '/api/royalty-cc-recipients', { emailAddress, displayName });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add CC recipient');
  }
  return (await response.json()).recipient;
}

export async function toggleCcRecipient(id: number, isActive: boolean): Promise<CcRecipient> {
  const response = await apiRequest('PATCH', `/api/royalty-cc-recipients/${id}`, { isActive });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update CC recipient');
  }
  return (await response.json()).recipient;
}
